/** @jsx jsx */

import { jsx, css } from '@emotion/core';
import { lighten } from 'polished';
import { ReactElement, Fragment, createContext, useContext } from 'react';
import { capitalize, get, isNaN } from 'lodash';

import { Colors } from '@zapier/theme';
import { Colors as DesignSystemColors, Text } from '@zapier/design-system';
import { pluralize } from '@zapier/common-utils';
import { Dateable, RelativeTime } from '@zapier/date';
import { FormLabel, SearchableSelectField } from '@zapier/common-components';

import Icon from 'app/common/components/Icon';
import LogoImage from 'app/common/components/LogoImage';
import fuzzysearch from 'app/common/fuzzysearch';
import { Div } from 'app/common/components/AccessibleElement';
import { getIconForAuthType } from 'app/auth/utils/AuthUtils';

export const ADD_NEW_ACCOUNT_VALUE = 0;

const Styles = {
  addNewMenuItemWrapper: css`
    position: relative;
    width: 100%;
  `,
  menuItem: css`
    display: flex;
    align-items: center;
    min-height: 42px;
    justify-content: center;
    color: ${Colors.heroine};
  `,
  menuItemWrapper: css`
    flex: 1;
    line-height: 1.5;
  `,
  authTypeContainer: css`
    display: flex;
    align-items: center;
  `,
  authTypeIcon: css`
    margin-left: 10px;
    margin-right: 5px;
    color: ${lighten(0.2, Colors.batmobile)};
  `,
  expired: css`
    color: ${DesignSystemColors.UiBrand};
  `,
  selectionComponent: css`
    display: flex;
    align-items: center;
  `,
  selectedComponentText: css`
    flex: 1;
    margin-left: 10px;
  `,
};

const AddNewAuthMenuItem = ({ label }) => {
  return (
    <div css={Styles.addNewMenuItemWrapper}>
      <div css={Styles.menuItem}>
        <Text fontWeight={600}>+ {label}</Text>
      </div>
    </div>
  );
};

const isValidDate = date => !isNaN(Date.parse(date));

const AddedDate = ({ date }) =>
  isValidDate(date) && (
    <span>
      {' '}
      Added <RelativeTime date={date} />
    </span>
  );

const MenuItem = ({ item }) => {
  if (item.value === ADD_NEW_ACCOUNT_VALUE) {
    return <AddNewAuthMenuItem label={item.label} />;
  }

  return (
    <Fragment>
      <div css={Styles.menuItemWrapper}>
        <Text color="Red4" fontWeight={600} tag="div">
          {item.isStale && 'EXPIRED - '}
          {item.label}
        </Text>
        <Text color="GrayWarm7" tag="div">
          {item.connectionLabel ? (
            item.connectionLabel
          ) : (
            <AddedDate date={item.addedDate} />
          )}{' '}
          (used in {item.numZaps} {pluralize('Zap', item.numZaps)})
        </Text>
      </div>
      <span css={Styles.authTypeContainer}>
        <Icon
          css={Styles.authTypeIcon}
          type={getIconForAuthType(item.authType)}
        />{' '}
        <span>{capitalize(item.authType)}</span>
      </span>
    </Fragment>
  );
};

const ApiContext = createContext('');

const SelectionComponent = props => {
  const api = useContext(ApiContext);

  const { selectedItem, isLoading } = props;
  const placeholder = isLoading ? 'Loading...' : props.placeholder;

  return (
    <Div css={Styles.selectionComponent}>
      <LogoImage isAppLogo={true} logoClass={api} logoSize={16} />
      <Text color="GrayWarm7" margin="0 0 0 10px" tag="div">
        {get(selectedItem, 'label', placeholder)}
      </Text>
    </Div>
  );
};

export type AuthSelectItem = {
  value: number;
  addedDate?: Dateable;
  isDisabled?: boolean;
  isStale?: boolean;
  iconAfter?: string;
  label: string;
};

type Props = {
  api: string;
  autoFocus?: boolean;
  labelElement: string | ReactElement;
  labelId: string;
  menuAriaLabel: string;
  items: AuthSelectItem[];
  onChange: (choice: AuthSelectItem) => void;
  selectedItem?: AuthSelectItem;
};

const filterItems = (inputValue, items) => {
  return items.filter(
    item =>
      item.value === ADD_NEW_ACCOUNT_VALUE ||
      fuzzysearch(inputValue, item.label)
  );
};

const AuthSelect = ({
  api,
  autoFocus,
  items,
  labelElement,
  labelId,
  menuAriaLabel,
  onChange,
  selectedItem,
}: Props) => {
  const allItems = [
    ...items,
    { value: ADD_NEW_ACCOUNT_VALUE, label: 'Add a New Account' },
  ];
  const ignoredChanges = [ADD_NEW_ACCOUNT_VALUE];

  const key = selectedItem
    ? `${selectedItem.value}-${selectedItem.label}`
    : 'no-selection';

  return (
    // SelectionComponent needs the api for the app logo. However, when initialSelectedItem
    // is undefined (no auth selected), selectedItem.api will blow up inside SelectionComponent.
    // The api is actually static in that it is the same for all items. So, stick it in
    // a context and let SelectionComponent consume it.
    <ApiContext.Provider value={api}>
      <FormLabel id={labelId}>{labelElement}</FormLabel>
      <SearchableSelectField
        autoComplete="off"
        autoFocus={autoFocus}
        filterItems={filterItems}
        ignoredChanges={ignoredChanges}
        initialSelectedItem={selectedItem}
        items={allItems}
        key={key}
        labelId={labelId}
        menuAriaLabel={menuAriaLabel}
        MenuItemComponent={MenuItem}
        onChange={onChange}
        placeholder="Search & Select ..."
        SelectionComponent={SelectionComponent}
        shouldSelectOnBlur={false}
      />
    </ApiContext.Provider>
  );
};

export default AuthSelect;
